import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

export interface ProductSection {
  id: number;
  descricao?: string;
  referencia?: string;
  isInativo: boolean;
}

export interface ProductSectionFormData {
  descricao?: string;
  referencia?: string;
  isInativo?: boolean;
}

export function useProductSections() {
  const queryClient = useQueryClient();

  const { data: sections = [], isLoading } = useQuery({
    queryKey: ["product-sections"],
    queryFn: async () => {
      const { data } = await apiClient.get<ProductSection[]>("/api/secoes-produto");
      return data;
    },
  });

  const createSection = useMutation({
    mutationFn: async (formData: ProductSectionFormData) => {
      const { data } = await apiClient.post<ProductSection>("/api/secoes-produto", formData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-sections"] });
      toast.success("Seção criada com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Erro ao criar seção: " + error.message);
    },
  });

  const updateSection = useMutation({
    mutationFn: async ({ id, ...formData }: ProductSectionFormData & { id: number }) => {
      const { data } = await apiClient.put<ProductSection>(`/api/secoes-produto/${id}`, formData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-sections"] });
      toast.success("Seção atualizada com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Erro ao atualizar seção: " + error.message);
    },
  });

  const deleteSection = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/api/secoes-produto/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-sections"] });
      toast.success("Seção inativada com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Erro ao inativar seção: " + error.message);
    },
  });

  return {
    sections,
    isLoading,
    createSection,
    updateSection,
    deleteSection,
  };
}
